/**
 * RFC 4180 CSV serialization with spreadsheet formula-injection protection.
 *
 * Two kinds of field exist and they are handled differently on purpose:
 *
 * - Untrusted text (product identifiers from the customer's catalog) is neutralized and
 *   quoted as needed.
 * - Generated values (decimals, statuses, labels produced by this application) are
 *   validated and written verbatim, so genuine numbers such as `-1.34` stay numeric and are
 *   never prefixed.
 */

/** UTF-8 byte-order mark. Spreadsheet applications use it to detect UTF-8 in CSV files. */
const CSV_BYTE_ORDER_MARK = String.fromCodePoint(0xfeff)
const CSV_LINE_TERMINATOR = "\r\n"

/**
 * Characters a spreadsheet may interpret as the start of a formula: `=`, `+`, `-`, `@`, and
 * their full-width forms (U+FF1D, U+FF0B, U+FF0D, U+FF20), which some locales normalize to
 * the ASCII equivalents. Written as code points so no invisible or look-alike literal
 * characters live in this file.
 */
const FORMULA_TRIGGER_CHARACTERS = new Set(
  [0x3d, 0x2b, 0x2d, 0x40, 0xff1d, 0xff0b, 0xff0d, 0xff20].map((codePoint) =>
    String.fromCodePoint(codePoint),
  ),
)

/**
 * Whitespace, control (Cc), and invisible format (Cf) characters, such as tab, carriage
 * return, NUL, non-breaking space, zero-width space, and the byte-order mark. Spreadsheets
 * skip many of these before evaluating a cell, so they cannot be allowed to hide a trigger.
 */
const LEADING_IGNORABLE_CHARACTERS = /^[\s\p{Cc}\p{Cf}]+/u

const NEEDS_QUOTING = /[",\r\n]|^\s|\s$/
const DECIMAL_PATTERN = /^-?\d+(?:\.\d+)?$/

/** OWASP also lists a leading tab, carriage return, or line feed as unsafe on its own. */
const UNSAFE_LEADING_CONTROL = /^[\t\r\n]/

/**
 * Applies the OWASP CSV-injection mitigation: text that starts with a tab, carriage return,
 * or line feed, or whose first significant character (after leading whitespace, control,
 * and invisible format characters) is a formula trigger, is prefixed with a single
 * apostrophe. The caller then quotes the field. The original characters are unchanged.
 *
 * Limits, stated by OWASP and not solved here: the apostrophe is part of the exported
 * value, so most applications show it as the first character; a spreadsheet application may
 * drop quotes or the prefix when the file is saved and re-opened; and no CSV sanitization
 * is safe for every spreadsheet application and downstream consumer.
 */
function neutralizeSpreadsheetFormula(text: string) {
  const firstSignificantCharacter = [...text.replace(LEADING_IGNORABLE_CHARACTERS, "")][0]
  const startsWithTrigger =
    firstSignificantCharacter !== undefined &&
    FORMULA_TRIGGER_CHARACTERS.has(firstSignificantCharacter)

  return startsWithTrigger || UNSAFE_LEADING_CONTROL.test(text)
    ? { text: `'${text}`, neutralized: true }
    : { text, neutralized: false }
}

function quoteCsvField(text: string) {
  return `"${text.replaceAll('"', '""')}"`
}

/** Serializes customer-derived text. `null` and `undefined` become an empty field. */
function serializeUntrustedCsvText(value: string | null | undefined) {
  if (value == null || value === "") return ""

  const { text, neutralized } = neutralizeSpreadsheetFormula(value)
  return neutralized || NEEDS_QUOTING.test(text) ? quoteCsvField(text) : text
}

/**
 * Serializes an exact decimal string produced by DuckDB. Trailing zeros beyond two decimal
 * places are removed (`96.0000` becomes `96.00`, `96.4375` stays), which never changes the value.
 * Anything that is not a plain decimal is rejected rather than written into a numeric column.
 */
function serializeCsvDecimal(value: string) {
  if (!DECIMAL_PATTERN.test(value)) throw new Error("Invalid decimal export value.")
  return value.replace(/(\.\d{2}\d*?)0+$/, "$1")
}

/** Serializes a value generated by this application that needs no neutralization. */
function serializeGeneratedCsvText(value: string) {
  return NEEDS_QUOTING.test(value) ? quoteCsvField(value) : value
}

function joinCsvRow(fields: readonly string[]) {
  return `${fields.join(",")}${CSV_LINE_TERMINATOR}`
}

export {
  CSV_BYTE_ORDER_MARK,
  CSV_LINE_TERMINATOR,
  joinCsvRow,
  neutralizeSpreadsheetFormula,
  serializeCsvDecimal,
  serializeGeneratedCsvText,
  serializeUntrustedCsvText,
}
